/**
 * The status grid's cells on live data (2026-10-17): one per bound state tag, read from its latest reading. A boolean
 * reads On at 0.5 and above and Off below (a bucket's mean of 0s and 1s, or the reading itself); an enum or label tag
 * reads its latest label; a number bound by an older board reads as itself. A tag with no reading, or one that did not
 * resolve, still has its cell, so a person sees what is bound. Pure.
 */
import type { LiveTagData } from "./use-live-tag-data";

export interface StatusCell {
  key: string;
  name: string;
  state: "on" | "off" | "label" | "none";
  text: string;
  /** When the reading was taken (ISO), or null with no reading. */
  time: string | null;
}

export function statusCells(keys: readonly string[], live: LiveTagData | undefined): StatusCell[] {
  return keys.map((key) => {
    const d = live?.[key.toLowerCase()];
    const none: StatusCell = { key, name: d?.name ?? key, state: "none", text: "No reading", time: null };
    if (!d) return none;
    if (d.latestLabel !== undefined) {
      return d.latestLabel === null ? none : { ...none, state: "label", text: d.latestLabel, time: d.latestTime };
    }
    if (d.latest === null || !Number.isFinite(d.latest)) return none;
    if (d.dataKind === "boolean_state") {
      const on = d.latest >= 0.5;
      return { ...none, state: on ? "on" : "off", text: on ? "On" : "Off", time: d.latestTime };
    }
    const value = d.latest.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return { ...none, state: "label", text: d.unit ? `${value} ${d.unit}` : value, time: d.latestTime };
  });
}
